import type { AxiosError } from "axios";
import { ApiError, extractApiErrorMessage } from "../../../utils";
import { ApiWebservice } from "../axios-config";
import {
	ISetupCharge,
	ISetupChargeCancelPayload,
	ISetupChargeConfig,
	ISetupChargeContactPayload,
	ISetupChargeCreatePayload,
	ISetupChargeErrorDetail,
	ISetupChargeListParams,
	ISetupChargeListResult,
	ISetupChargeManualPaymentPayload,
	ISetupChargePartner,
	ISetupChargePartnerPayload,
	ISetupChargeQueueProblems,
	ISetupChargeRepassPayload,
	ISetupChargeRequest,
	ISetupChargeRequestListParams,
	ISetupChargeRequestListResult,
	ISetupChargeWebhookDelivery,
} from "./Types";

const BASE_URL = "/v1/panel/setup-charges";
const REQUESTS_URL = "/v1/panel/requests";

/** Erro da API do módulo, com os detalhes por campo (`details`) quando o back devolver. */
export class SetupChargeApiError extends ApiError {
	details: ISetupChargeErrorDetail[];

	constructor(message: string, options?: { code?: string; status?: number; details?: ISetupChargeErrorDetail[] }) {
		super(message, options);
		this.name = "SetupChargeApiError";
		this.details = options?.details ?? [];
	}
}

/**
 * Todas as chamadas devolvem o resultado ou um erro com a mensagem que o back mandou,
 * para a tela mostrar o motivo exato, sem texto genérico (o fallback só vale quando a API não respondeu).
 */
const toApiError = (error: unknown, fallback: string): SetupChargeApiError => {
	const base = extractApiErrorMessage(error, fallback);
	const body = (error as AxiosError)?.response?.data as { details?: ISetupChargeErrorDetail[] } | undefined;
	return new SetupChargeApiError(base.message, {
		code: base.code,
		status: base.status,
		details: Array.isArray(body?.details) ? body?.details : [],
	});
};

const withoutEmpty = (params: object): Record<string, unknown> =>
	Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

const list = async (params: ISetupChargeListParams = {}): Promise<ISetupChargeListResult | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(BASE_URL, { params: withoutEmpty(params) });
		return { data: data?.results ?? [], totalCount: data?.total ?? 0, totals: data?.totals ?? undefined };
	} catch (error) {
		return toApiError(error, "Não foi possível consultar as taxas avulsas.");
	}
};

const getById = async (id: string): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(`${BASE_URL}/${id}`);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível consultar a taxa avulsa.");
	}
};

/** Cria a taxa pelo painel. A API responde 202 com o `requestId`; o resultado vem por `getRequest`. */
const create = async (payload: ISetupChargeCreatePayload): Promise<{ requestId: string } | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(BASE_URL, payload);
		const requestId = String(data?.requestId ?? "");
		if (!requestId) return new SetupChargeApiError("A API não devolveu o identificador da solicitação.");
		return { requestId };
	} catch (error) {
		return toApiError(error, "Não foi possível enviar a solicitação de criação da taxa avulsa.");
	}
};

const getRequest = async (requestId: string): Promise<ISetupChargeRequest | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(`${REQUESTS_URL}/${requestId}`);
		return data as ISetupChargeRequest;
	} catch (error) {
		return toApiError(error, "Não foi possível consultar o andamento da solicitação.");
	}
};

const getConfig = async (clientId: string): Promise<ISetupChargeConfig | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(`${BASE_URL}/config/${clientId}`);
		return data as ISetupChargeConfig;
	} catch (error) {
		return toApiError(error, "Não foi possível consultar a configuração da taxa avulsa da imobiliária.");
	}
};

const cancel = async (id: string, payload: ISetupChargeCancelPayload): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${BASE_URL}/${id}/cancel`, payload);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível cancelar a taxa avulsa.");
	}
};

const resend = async (id: string): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${BASE_URL}/${id}/resend`);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível reenviar o link da taxa avulsa.");
	}
};

const updateContact = async (id: string, payload: ISetupChargeContactPayload): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().patch(`${BASE_URL}/${id}/contact`, payload);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível corrigir o contato da taxa avulsa.");
	}
};

const manualPayment = async (id: string, payload: ISetupChargeManualPaymentPayload): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${BASE_URL}/${id}/manual-payment`, payload);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível dar baixa na taxa avulsa.");
	}
};

const repass = async (id: string, payload: ISetupChargeRepassPayload): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${BASE_URL}/${id}/repasse`, payload);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível registrar o repasse da taxa avulsa.");
	}
};

const reprocess = async (id: string): Promise<ISetupCharge | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${BASE_URL}/${id}/reprocess`);
		return data as ISetupCharge;
	} catch (error) {
		return toApiError(error, "Não foi possível reprocessar a taxa avulsa.");
	}
};

const saveConfig = async (clientId: string, payload: Partial<ISetupChargeConfig>): Promise<ISetupChargeConfig | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().put(`${BASE_URL}/config/${clientId}`, payload);
		return data as ISetupChargeConfig;
	} catch (error) {
		return toApiError(error, "Não foi possível salvar a configuração da taxa avulsa da imobiliária.");
	}
};

const listRequests = async (params: ISetupChargeRequestListParams = {}): Promise<ISetupChargeRequestListResult | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(REQUESTS_URL, { params: withoutEmpty(params) });
		return { data: data?.results ?? [], totalCount: data?.total ?? 0 };
	} catch (error) {
		return toApiError(error, "Não foi possível consultar as solicitações.");
	}
};

const getQueueProblems = async (): Promise<ISetupChargeQueueProblems | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get("/v1/panel/queue/problems");
		return { requests: data?.requests ?? [], deliveries: data?.deliveries ?? [] };
	} catch (error) {
		return toApiError(error, "Não foi possível consultar os itens da fila com problema.");
	}
};

const retryRequest = async (requestId: string): Promise<ISetupChargeRequest | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${REQUESTS_URL}/${requestId}/retry`);
		return data as ISetupChargeRequest;
	} catch (error) {
		return toApiError(error, "Não foi possível reprocessar a solicitação.");
	}
};

const retryDelivery = async (deliveryId: string): Promise<{ id: string } | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`/v1/panel/webhook-deliveries/${deliveryId}/retry`);
		return data as { id: string };
	} catch (error) {
		return toApiError(error, "Não foi possível reenviar a entrega do webhook.");
	}
};

const PARTNERS_URL = "/v1/panel/partners";

const listPartners = async (): Promise<ISetupChargePartner[] | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(PARTNERS_URL);
		return Array.isArray(data) ? data : [];
	} catch (error) {
		return toApiError(error, "Não foi possível consultar os parceiros.");
	}
};

const getPartner = async (partner: string): Promise<ISetupChargePartner | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(`${PARTNERS_URL}/${encodeURIComponent(partner)}`);
		return data as ISetupChargePartner;
	} catch (error) {
		return toApiError(error, "Não foi possível consultar o parceiro.");
	}
};

const savePartner = async (partner: string, payload: ISetupChargePartnerPayload): Promise<ISetupChargePartner | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().put(`${PARTNERS_URL}/${encodeURIComponent(partner)}`, payload);
		return data as ISetupChargePartner;
	} catch (error) {
		return toApiError(error, "Não foi possível salvar o parceiro.");
	}
};

/** Gera um novo segredo de assinatura; o valor só vem nesta resposta e nunca mais pode ser consultado. */
const rotatePartnerSecret = async (partner: string): Promise<ISetupChargePartner | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${PARTNERS_URL}/${encodeURIComponent(partner)}/rotate-secret`);
		return data as ISetupChargePartner;
	} catch (error) {
		return toApiError(error, "Não foi possível gerar o segredo do webhook.");
	}
};

const testPartnerWebhook = async (partner: string): Promise<{ queued: boolean } | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().post(`${PARTNERS_URL}/${encodeURIComponent(partner)}/test-webhook`);
		return data as { queued: boolean };
	} catch (error) {
		return toApiError(error, "Não foi possível enviar o teste do webhook.");
	}
};

const getPartnerDeliveries = async (partner: string): Promise<ISetupChargeWebhookDelivery[] | SetupChargeApiError> => {
	try {
		const { data } = await ApiWebservice().get(`${PARTNERS_URL}/${encodeURIComponent(partner)}/deliveries`);
		return Array.isArray(data) ? data : [];
	} catch (error) {
		return toApiError(error, "Não foi possível consultar as entregas do webhook.");
	}
};

export const SetupChargeService = {
	saveConfig,
	listRequests,
	getQueueProblems,
	retryRequest,
	retryDelivery,
	listPartners,
	getPartner,
	savePartner,
	rotatePartnerSecret,
	testPartnerWebhook,
	getPartnerDeliveries,
	list,
	getById,
	create,
	getRequest,
	getConfig,
	cancel,
	resend,
	updateContact,
	manualPayment,
	repass,
	reprocess,
};

export type { ISetupCharge } from "./Types";
